import { Colors } from "@/enums/Colors";
import { GeneralFileNodeType } from "@/enums/DocumentCenterFileType";
import { DocumentCenterFileSmall } from "@/models/DocumentCenterFileSmall";
import DocumentCenterFileService from "@/services/DocumentCenterFileService";
import { Box, listClasses, listItemClasses, styled, typographyClasses } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import FileBrowserRecursiveList from "./FileBrowserRecursiveList";

type Props = {
  tenantExternalId?: string;
  lazoUserExternalId?: string;
  setSelectedFolder: Dispatch<SetStateAction<DocumentCenterFileSmall>>;
  selectedFolder: DocumentCenterFileSmall;
};

function FileBrowserRecursiveListWrapper(props: Props) {
  const [folders, setFolders] = useState<DocumentCenterFileSmall[]>([]);

  useEffect(() => {
    setFolders([]);
    fetchFolders([], null);
  }, [props.tenantExternalId, props.lazoUserExternalId]);

  function checkFetchedAndSelectFolder(positions: number[]) {
    if (positions.length <= 0) return;
    let folder = folders[positions[0]];
    for (let i of positions.slice(1, positions.length)) {
      folder = folder.children[i];
    }
    props.setSelectedFolder(folder);
    return folder.children !== undefined && folder.children !== null;
  }

  function handleFolderClick(pos: string, externalId: string, refresh?: boolean) {
    const positions: number[] = pos ? pos.split(",").map((p) => Number(p)) : [];
    if (!refresh && checkFetchedAndSelectFolder(positions)) return;
    fetchFolders(positions, externalId);
  }

  function fetchFolders(positions: number[], externalId: string) {
    DocumentCenterFileService.getSmallByParentIdAndTypeAndUserAndTenant(
      externalId,
      GeneralFileNodeType.FOLDER,
      props.tenantExternalId,
      props.lazoUserExternalId
    ).then((r) => {
      r.onSuccess((f) => {
        if (folders.length === 0) {
          setFolders([{ name: "Home", externalId: null, children: f }]);
        } else {
          propagateFolders(positions, f);
        }
      });
    });
  }

  function propagateFolders(positions: number[], files: DocumentCenterFileSmall[]) {
    if (positions.length <= 0) return;
    const clonedFolders = structuredClone(folders);
    let folder = clonedFolders[positions[0]];
    for (let i of positions.slice(1, positions.length)) {
      folder = folder.children[i];
    }
    folder.children = files;
    setFolders(clonedFolders);
  }

  return (
    <RecursiveListContainer>
      <FileBrowserRecursiveList
        collapsed={true}
        pos={""}
        options={folders}
        onItemClick={handleFolderClick}
        selectedFolder={props.selectedFolder}
        tenantExternalId={props.tenantExternalId}
        lazoUserExternalId={props.lazoUserExternalId}
      />
    </RecursiveListContainer>
  );
}

const RecursiveListContainer = styled(Box)(() => ({
  maxHeight: "40vh",
  overflow: "hidden",
  overflowY: "auto",
  display: "flex",
  alignItems: "start",
  flexDirection: "column",
  width: "100%",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  padding: 0,
  borderRadius: "0.5rem",
  [`& .${listClasses.root}`]: {
    paddingTop: 0,
    paddingBottom: 0,
    width: "100%",
    [`& .${listItemClasses.root}`]: {
      gap: "0.25rem",
      height: "1.5rem",
      ":hover": {
        cursor: "pointer",
        backgroundColor: `${Colors.F7F7F8_GRAY}`,
      },
    },
    [`& .${typographyClasses.root}`]: {
      fontSize: "0.875rem",
      fontWeight: 500,
    },
  },
}));

export default FileBrowserRecursiveListWrapper;
